import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/ServiceItemPage.css';
import axios from '../../axiosConfig';
import TechnicianCard from '../../components/TechnicianCard';
import imgPrefix from '../../imgConfig'; // 导入 imgConfig.js
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as solidHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as regularHeart } from '@fortawesome/free-regular-svg-icons';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';


const ServiceItemPage = () => {
  const { serviceId } = useParams();
  const { t, i18n } = useTranslation();
  const { backgroundColor } = useTheme();
  const primaryColor = backgroundColor || '#e60012';
  const [service, setService] = useState(null);
  const [technicians, setTechnicians] = useState([]); // Final technician details
  const navigate = useNavigate();
  const [serviceDuration, setServiceDuration] = useState("60"); // New state for selected technician
  const [selectedTechnicianIndex, setSelectedTechnicianIndex] = useState(0); // 使用索引跟踪选中的技师
  const [calculatedPrice, setCalculatedPrice] = useState(null); // New state for calculated price
  const [isFavorite, setIsFavorite] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 检查项目是否已被收藏
  const checkIfFavorited = useCallback(async (userId, type, objectId) => {
    try {
      const response = await axios.get(`/favorite/check?userId=${userId}&type=${type}&objectId=${objectId}`);
      if (response.data.code === 0) {
        setIsFavorite(response.data.data);
      }
    } catch (error) {
      console.error('检查收藏状态失败:', error);
    }
  }, []);

  // 获取用户信息 - 只在组件挂载时执行
  useEffect(() => {
    console.log('组件挂载，获取用户信息');
    // 从localStorage获取用户信息
    let storedUser = localStorage.getItem('userData');
    
    // 如果userData不存在，尝试使用user键
    if (!storedUser) {
      storedUser = localStorage.getItem('user');
    }
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } catch (err) {
        console.error('Error parsing user data', err);
      }
    }
  }, []); 

  // 检查是否收藏 - 当用户信息和服务信息都加载完成时执行
  useEffect(() => {
    if (userData && userData.id && service && service.id) {
      console.log('检查收藏状态');
      checkIfFavorited(userData.id, 'project', service.id);
    }
  }, [userData, service, checkIfFavorited]);

  // 切换收藏状态
  const toggleFavorite = useCallback(async () => {
    // 先检查localStorage中是否有token
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('请先登录后再收藏');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    // 再检查用户数据是否可用
    if (!userData || !userData.id) {
      // 检查localStorage是否有用户数据
      let storedUser = localStorage.getItem('userData');
      if (!storedUser) {
        storedUser = localStorage.getItem('user');
      }
      
      if (!storedUser) {
        toast.error('登录信息已过期，请重新登录');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);
        if (!parsedUser || !parsedUser.id) {
          toast.error('登录信息不完整，请重新登录');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
        // 更新userData状态
        setUserData(parsedUser);
      } catch (err) {
        toast.error('登录信息已损坏，请重新登录');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
    }

    if (!service) {
      toast.error('服务信息加载中，请稍后再试');
      return;
    }

    try {
      const userIdToUse = userData ? userData.id : JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user')).id;
      
      if (isFavorite) {
        // 取消收藏
        const response = await axios.delete(`/favorite/delete?userId=${userIdToUse}&type=project&objectId=${service.id}`);
        if (response.data.code === 0) {
          setIsFavorite(false);
          toast.success('已取消收藏');
        } else {
          toast.error(response.data.msg || '取消收藏失败');
        }
      } else {
        // 添加收藏
        const favoriteData = {
          userId: userIdToUse,
          type: 'project',
          objectId: service.id,
          objectName: service.xiangmumingcheng,
          objectImage: service.xiangmutupian
        };
        
        const response = await axios.post('/favorite/add', favoriteData);
        if (response.data.code === 0) {
          setIsFavorite(true);
          toast.success('收藏成功');
        } else {
          toast.error(response.data.msg || '收藏失败');
        }
      }
    } catch (error) {
      console.error('收藏操作失败:', error);
      toast.error('操作失败，请稍后再试');
    }
  }, [userData, service, isFavorite, navigate]);

  // 加载服务和技师信息 - 只依赖于serviceId
  useEffect(() => {
    console.log('开始加载服务详情，serviceId:', serviceId);
    setIsLoading(true);
    const currentLang = i18n.language || 'zh';
    
    // Fetch service details
    axios
      .get(`/jianshenxiangmu/detail/${serviceId}?lang=${currentLang}`)
      .then((response) => {
        console.log('服务详情加载成功');
        const serviceData = response.data.data;
        setService(serviceData);
        if (serviceData && serviceData.jiage) {
          let price = serviceData.jiage;
          setCalculatedPrice(price);
        }
        // 为确保按“服务名称”查教练在多语言下仍返回正常，这里单独以中文获取一次服务名称用于查询
        console.log('开始加载技师列表');
        return axios
          .get(`/jianshenxiangmu/detail/${serviceId}?lang=zh`)
          .then((zhRes) => {
            const zhServiceName = zhRes?.data?.data?.xiangmumingcheng || serviceData.xiangmumingcheng;
            return axios.get(`/jianshenxiangmu/${encodeURIComponent(zhServiceName)}/coaches?lang=${currentLang}`);
          });
      })
      .then((response) => {
        console.log('技师列表加载成功');
        const coaches = response.data.data;

        // Fetch detailed information for each coach
        console.log('开始加载技师详情');
        const technicianDetailsPromises = coaches.map((coach) =>
          axios.get(`/jiaolianxinxi/${coach.jiaolianbianhao}/detail?lang=${currentLang}`).then((res) => res.data.data)
        );

        // Resolve all technician details and update state
        return Promise.all(technicianDetailsPromises);
      })
      .then((detailedTechnicians) => {
        console.log('技师详情加载成功');
        const technicianList = detailedTechnicians.flat();
        console.log('设置技师列表，数量:', technicianList.length);
        setTechnicians(technicianList);
        // 默认选择第一个技师
        setSelectedTechnicianIndex(0);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('加载服务或技师信息失败:', error);
        setIsLoading(false);
      });
  }, [serviceId, i18n.language]); 

  // 计算价格 - 依赖于服务和时长
  useEffect(() => {
    if (service && service.jiage) {
      let price = service.jiage;
      if (serviceDuration === "90") {
        price *= 1.5;
      } else if (serviceDuration === "120") {
        price *= 2;
      }
      setCalculatedPrice(price);
    }
  }, [service, serviceDuration]);
  
  // 选择技师处理函数
  const handleSelectTechnician = useCallback((index) => {
    if (!technicians || index >= technicians.length) {
      console.error('技师列表为空或索引无效');
      return;
    }
    
    console.log('选择技师索引:', index);
    setSelectedTechnicianIndex(index);
    
    try {
      toast.success(`已选择技师: ${technicians[index].jiaolianxingming}`);
    } catch (error) {
      console.error('无法获取技师名称:', error);
    }
  }, [technicians]);

  // 监听技师列表变化，确保索引有效
  useEffect(() => {
    if (technicians.length > 0 && selectedTechnicianIndex >= technicians.length) {
      console.log('技师索引超出范围，重置为0');
      setSelectedTechnicianIndex(0);
    }
  }, [technicians, selectedTechnicianIndex]);

  // 使用useMemo计算当前选中的技师
  const selectedTechnician = useMemo(() => {
    if (technicians.length > 0 && selectedTechnicianIndex < technicians.length) {
      return technicians[selectedTechnicianIndex];
    }
    return null;
  }, [technicians, selectedTechnicianIndex]);

  if (isLoading || !service) return <div className="loading">加载中...</div>;

  return (
    <div className="service-item-page">
      <ToastContainer position="top-center" autoClose={2000} />
      
      {/* 后退按钮 */}
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {/* <span>返回</span> */}
        </button>
      </div>
      
      {/* Service details */}
      <div className="service-header">
        <img
          className="service-image"
          src={`${imgPrefix}/${service.xiangmutupian}`}
          alt={service.xiangmumingcheng}
        />
        <div className="service-title-container">
          <h1>{service.xiangmumingcheng}</h1>
          <button 
            className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
            onClick={toggleFavorite}
          >
            <FontAwesomeIcon icon={isFavorite ? solidHeart : regularHeart} />
            {isFavorite ? '已收藏' : '收藏'}
          </button>
        </div>
      </div>
      <p dangerouslySetInnerHTML={{ __html: service.xiangmujieshao.replace(/<[^>]*>/g, '') }}></p>
      <p className="tedian">{t('servicesPage.features')}: {service.tedian}</p>
      <p className="price">{t('servicesPage.price')}: {calculatedPrice}元</p>
      <p className="pingjia">{t('servicesPage.goodRate')}99% {t('servicesPage.sold')}125+</p>
      <div className="service-duration">
        <h2>{t('servicesPage.duration')}</h2>
        <label>
          <input
            type="radio"
            value="60"
            checked={serviceDuration === "60"}
            onChange={() => setServiceDuration("60")}
          />
          60{t('technicianPage.minute')}
        </label>
        <label>
          <input
            type="radio"
            value="90"
            checked={serviceDuration === "90"}
            onChange={() => setServiceDuration("90")}
          />
          90{t('technicianPage.minute')}
        </label>
        <label>
          <input
            type="radio"
            value="120"
            checked={serviceDuration === "120"}
            onChange={() => setServiceDuration("120")}
          />
          120{t('technicianPage.minute')}
        </label>
      </div>

      {/* Recommended Technicians */}
      <div className="technicians">
        <h2>{t('servicesPage.recommendedTechnicians')}</h2>
        {technicians.length > 0 ? (
          <>
            <p className="technician-hint">{t('servicesPage.selectTechnicianTip')}</p>
            <div className="technician-list">
              {technicians.map((technician, index) => (
                <div
                  key={technician.id || index}
                  className={`technician-card ${index === selectedTechnicianIndex ? 'selected' : ''}`}
                  onClick={() => handleSelectTechnician(index)}
                  style={{
                    border: index === selectedTechnicianIndex ? `2px solid ${primaryColor}` : '2px solid transparent',
                    backgroundColor: index === selectedTechnicianIndex ? '#fff0f0' : 'transparent',
                    transform: index === selectedTechnicianIndex ? 'translateY(-5px)' : 'none',
                    boxShadow: index === selectedTechnicianIndex ? '0 5px 15px rgba(0,0,0,0.2)' : '0 2px 5px rgba(0,0,0,0.1)'
                  }}
                >
                  <TechnicianCard
                    distance={`${technician.jiaoliandengji}km`}
                    name={technician.jiaolianxingming}
                    image={`${technician.jiaoliantupian}`}
                    isSelected={index === selectedTechnicianIndex}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="no-technicians">{t('servicesPage.noTechnicians')}</p>
        )}
      </div>
      <button
        className="reserve-button"
        onClick={() => {
          if (!selectedTechnician) {
            toast.error(t('servicesPage.selectTechnicianFirst'));
            return;
          }
          navigate(`/technician/${selectedTechnician.jiaolianbianhao}`, {
            state: {
              service,
              technician: selectedTechnician,
              serviceDuration,
              calculatedPrice,
            },
          });
        }}
      >
        {t('servicesPage.bookNow')}
      </button>
    </div>
  );
};

export default ServiceItemPage;
